import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPriceChangesData, postPriceChangesData } from "src/api/sp";
import { resetStore } from "src/store/actions";
import stringToNumber from "src/utils/stringToNumber";
import { formatToCurrency } from "src/utils/genericFunctions";

export const fetchPriceChangesData = createAsyncThunk(
  "dashboard/fetchPriceChangesData",
  ({
    department,
    storeId,
    startDate,
    endDate
  }: {
    department: string;
    storeId: string;
    startDate: string;
    endDate: string;
  }) => {
    return getPriceChangesData({ department, storeId, startDate, endDate });
  }
);

export const postSnoozeStatus = createAsyncThunk(
  "dashboard/postSnoozeStatus",
  ({
    department,
    storeId,
    row,
    currentDate,
    createdBy
  }: {
    department: string;
    storeId: string;
    row: any;
    currentDate: string;
    createdBy: string;
  }) =>
    postPriceChangesData({
      department,
      storeId,
      row,
      currentDate,
      createdBy
    })
);

const initialState = {
  status: "initial",
  data: [] as any[],
  error: "",
  loadingItem: "",
  snoozeInProgress: false
};

const priceChangesTileSlice = createSlice({
  name: "priceChangesTile",
  initialState,
  reducers: {
    toggleSnooze: (state, action) => {
      state.data = [...state.data, action.payload];
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, () => {
        return initialState;
      })
      .addCase(fetchPriceChangesData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchPriceChangesData.fulfilled, (state, { payload }) => {
        state.status = "idle";
        if (!payload?.data?.length) {
          state.data = null;
          return;
        }
        const data = payload?.data.map((list: any) => ({
          ...list,
          newRetail: formatToCurrency(list.newRetail),
          currentRetail: formatToCurrency(list.currentRetail)
        }));
        state.data = getFilterSortedSnoozeList(data);
      })
      .addCase(fetchPriceChangesData.rejected, (state) => {
        state.status = "error";
        state.data = null;
      })
      .addCase(postSnoozeStatus.pending, (state, action) => {
        state.snoozeInProgress = true;
        const args = action.meta.arg;
        state.loadingItem = args?.row?.itemId;
      })
      .addCase(postSnoozeStatus.fulfilled, (state, action) => {
        const args = action.meta.arg;
        const list = state?.data?.map((item) => {
          if (item?.itemId === args?.row?.itemId) {
            return { ...item, isSnoozed: !args?.row?.isSnoozed };
          }
          return item;
        });
        state.data = getFilterSortedSnoozeList(list);
        state.loadingItem = "";
      })
      .addCase(postSnoozeStatus.rejected, (state) => {
        state.loadingItem = "";
      });
  }
});

const getFilterSortedSnoozeList = <T>(list: T[]) => {
  const notSnoozedItems = list.filter((item: any) => !item.isSnoozed);

  const snoozedItems = list.filter((item: any) => item.isSnoozed);

  const snoozedPriceIncreaseItems = snoozedItems.filter(
    (item: any) =>
      stringToNumber(item.newRetail) > stringToNumber(item.currentRetail)
  );
  const snoozedPriceDecreaseItems = snoozedItems.filter(
    (item: any) =>
      stringToNumber(item.newRetail) <= stringToNumber(item.currentRetail)
  );

  const notSnoozedPriceIncreaseItems = notSnoozedItems.filter(
    (item: any) =>
      stringToNumber(item.newRetail) > stringToNumber(item.currentRetail)
  );
  const notSnoozedPriceDecreaseItems = notSnoozedItems.filter(
    (item: any) =>
      stringToNumber(item.newRetail) <= stringToNumber(item.currentRetail)
  );

  notSnoozedPriceIncreaseItems.sort((a: any, b: any) => {
    return (
      stringToNumber(b.newRetail) -
      stringToNumber(b.currentRetail) -
      (stringToNumber(a.newRetail) - stringToNumber(a.currentRetail))
    );
  });

  notSnoozedPriceDecreaseItems.sort(
    (a: any, b: any) =>
      stringToNumber(b.currentRetail) -
      stringToNumber(b.newRetail) -
      (stringToNumber(a.currentRetail) - stringToNumber(a.newRetail))
  );

  snoozedPriceIncreaseItems.sort(
    (a: any, b: any) =>
      stringToNumber(b.newRetail) -
      stringToNumber(b.currentRetail) -
      (stringToNumber(a.newRetail) - stringToNumber(a.currentRetail))
  );

  snoozedPriceDecreaseItems.sort(
    (a: any, b: any) =>
      stringToNumber(b.currentRetail) -
      stringToNumber(b.newRetail) -
      (stringToNumber(a.currentRetail) - stringToNumber(a.newRetail))
  );
  return [
    ...notSnoozedPriceIncreaseItems,
    ...notSnoozedPriceDecreaseItems,
    ...snoozedPriceIncreaseItems,
    ...snoozedPriceDecreaseItems
  ];
};

export default priceChangesTileSlice.reducer;
export const { toggleSnooze } = priceChangesTileSlice.actions;
